/**
 * Google Analytics 4 Integration
 * 
 * This module provides functions to track user interactions and page views
 * using Google Analytics 4.
 */

// GA4 Measurement ID from environment variables
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

/**
 * Initialize Google Analytics
 * Call this once when the app loads
 */
export const initGA = (): void => {
  if (!GA_MEASUREMENT_ID) {
    console.warn('Google Analytics Measurement ID not configured');
    return;
  }

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
    send_page_view: true,
  });

  console.log('Google Analytics initialized:', GA_MEASUREMENT_ID);
};

/**
 * Track page view
 * Call this on route changes
 */
export const trackPageView = (url: string): void => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: url,
  });
};

/**
 * Track custom event
 * Generic function for tracking any event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;
  
  window.gtag('event', eventName, eventParams);
};

/**
 * Track form submission
 * Use this when a form is successfully submitted
 */
export const trackFormSubmit = (formName: string): void => {
  trackEvent('form_submit', {
    form_name: formName,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track button click
 * Use this for important button interactions
 */
export const trackButtonClick = (buttonName: string, buttonLocation?: string): void => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation,
  });
};

/**
 * Track PDF download
 * Use this when a user downloads or views a PDF
 */
export const trackPDFDownload = (pdfName: string): void => {
  trackEvent('file_download', {
    file_name: pdfName,
    file_type: 'pdf',
    file_extension: '.pdf',
  });
};

/**
 * Track service view
 * Use this when a user views a service detail page
 */
export const trackServiceView = (serviceName: string): void => {
  trackEvent('view_item', {
    item_name: serviceName,
    item_category: 'service',
  });
};

/**
 * Track external link click
 * Use this when a user clicks on an external link
 */
export const trackExternalLink = (url: string, linkText?: string): void => {
  trackEvent('click', {
    link_url: url,
    link_text: linkText,
    link_domain: new URL(url).hostname,
    outbound: true,
  });
};

/**
 * Track search
 * Use this if you implement a search feature
 */
export const trackSearch = (searchTerm: string): void => {
  trackEvent('search', {
    search_term: searchTerm,
  });
};

/**
 * Track video play
 * Use this when a user plays a video
 */
export const trackVideoPlay = (videoTitle: string): void => {
  trackEvent('video_start', {
    video_title: videoTitle,
  });
};

/**
 * Check if Google Analytics is configured
 */
export const isGAConfigured = (): boolean => {
  return !!GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== '';
};

// Type declarations for window.gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
